/**
 * Unified login on `app.<base>` — the Account Tray door (ADR-0077 S2).
 *
 * Email + password authenticate the CENTRAL identity (Control), then the workspace
 * picker lists the memberships; picking one mints the cross-subdomain handoff and
 * leaves the SPA for `<slug>.<base>`. One membership (or a `?next=<slug>` return-to
 * that matches) skips the picker — the IdP-redirect feel. A stored identity session
 * on mount skips the form entirely. Local accounts (username, no email) sign in on
 * their workspace's subdomain — this door is central-only by design.
 */
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  identityApi,
  identityToken,
  setIdentityToken,
  type IdentityWorkspace,
  OnboardingApiError,
  type IdentityLoginResult,
} from "@/lib/api-onboarding";

/** The self-serve signup page (path-based + onboarding-host root alias). */
const SIGNUP_PATH = "/onboarding";

type Stage =
  | { kind: "boot" }
  | { kind: "credentials" }
  | { kind: "totp"; challengeToken: string }
  | { kind: "pick"; workspaces: IdentityWorkspace[]; email: string }
  | { kind: "confirmCentral"; email: string }
  | { kind: "forgot" };

export function UnifiedLoginPage() {
  const { t } = useTranslation();

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [code, setCode] = React.useState("");
  const [stage, setStage] = React.useState<Stage>({ kind: "boot" });
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  const next = React.useMemo(() => new URLSearchParams(window.location.search).get("next"), []);
  // `?continue=central` (SSO, 2026-08-01): the visitor came from Tray Central, which
  // has no login of its own — once the identity is authenticated HERE, skip the
  // workspace picker entirely and hand off to `central.<base>` instead.
  const continueCentral = React.useMemo(
    () => new URLSearchParams(window.location.search).get("continue") === "central",
    [],
  );
  // `?picker=1` (the workspace SWITCHER, ADR-0077 S4): always show the picker —
  // without it a single-membership identity would auto-handoff straight back
  // into the workspace it just came from.
  const forcePicker = React.useMemo(
    () => new URLSearchParams(window.location.search).get("picker") === "1",
    [],
  );

  /** Enter a workspace: handoff cookie + hard navigation onto the tenant subdomain. */
  const enter = React.useCallback(async (slug: string) => {
    setBusy(true);
    try {
      const res = await identityApi.handoff(slug);
      window.location.href = res.redirect_url;
    } catch {
      setError("Accesso al workspace non riuscito. Riprova.");
      setBusy(false);
    }
  }, []);

  /** Leave for Tray Central: mint the central handoff and follow its redirect. */
  const enterCentral = React.useCallback(async () => {
    setBusy(true);
    try {
      const res = await identityApi.centralHandoff();
      window.location.href = res.redirect_url;
    } catch {
      setError("Accesso a Tray Central non riuscito. Riprova.");
      setBusy(false);
      setStage({ kind: "credentials" });
    }
  }, []);

  /** Route the picker: `?next` match or a single membership → straight in
   *  (unless `?picker=1` — the switcher wants the choice on screen).
   *  `?continue=central` wins over everything: the person is not here to pick a
   *  workspace at all. But when the session PRE-EXISTS (boot, not a login just
   *  typed), the handoff is not instant: after a logout on Central an invisible
   *  re-entry reads as "il logout non ha funzionato" — so we ask "continui come
   *  X?" first (the Google account-confirmation pattern). A fresh login skips
   *  the question: the person proved who they are seconds ago. */
  const routeWorkspaces = React.useCallback(
    (workspaces: IdentityWorkspace[], authedEmail: string, existingSession = false) => {
      if (continueCentral) {
        if (existingSession) {
          setStage({ kind: "confirmCentral", email: authedEmail });
          setBusy(false);
          return;
        }
        void enterCentral();
        return;
      }
      const target = forcePicker
        ? null
        : (next && workspaces.find((w) => w.slug === next)) ||
          (workspaces.length === 1 ? workspaces[0] : null);
      if (target) {
        void enter(target.slug);
        return;
      }
      setStage({ kind: "pick", workspaces, email: authedEmail });
      setBusy(false);
    },
    [enter, enterCentral, next, forcePicker, continueCentral],
  );

  // Already signed in (a stored identity session) → skip the form (IdP feel).
  const bootedRef = React.useRef(false);
  React.useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;
    void (async () => {
      if (!identityToken()) {
        setStage({ kind: "credentials" });
        return;
      }
      try {
        const me = await identityApi.me();
        routeWorkspaces(me.workspaces, me.email, true);
      } catch {
        setIdentityToken(null); // dead/stale session → the normal form
        setStage({ kind: "credentials" });
      }
    })();
  }, [routeWorkspaces]);

  function routeLogin(res: IdentityLoginResult) {
    if (res.status === "totp_required" && res.challenge_token) {
      setStage({ kind: "totp", challengeToken: res.challenge_token });
      setBusy(false);
      return;
    }
    if (res.token) {
      setIdentityToken(res.token);
      routeWorkspaces(res.workspaces ?? [], res.email ?? email);
      return;
    }
    setBusy(false);
    setError(t("unifiedLogin.errorGeneric", "Accesso non riuscito. Riprova."));
  }

  function mapError(err: unknown): string {
    if (err instanceof OnboardingApiError) {
      if (err.status === 401)
        return t("unifiedLogin.invalidCredentials", "Credenziali non valide.");
      if (err.status === 429) return "Troppi tentativi. Attendi qualche secondo e riprova.";
      return err.message;
    }
    return t("unifiedLogin.errorGeneric", "Accesso non riuscito. Riprova.");
  }

  async function handleCredentials(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      routeLogin(await identityApi.login({ email: email.trim().toLowerCase(), password }));
    } catch (err) {
      setError(mapError(err));
      setBusy(false);
    }
  }

  async function handleTotp(event: React.FormEvent) {
    event.preventDefault();
    if (stage.kind !== "totp") return;
    setError(null);
    setBusy(true);
    try {
      routeLogin(
        await identityApi.loginTotp({ challenge_token: stage.challengeToken, code: code.trim() }),
      );
    } catch (err) {
      setError(mapError(err));
      setBusy(false);
    }
  }

  async function handleForgot(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      const res = await identityApi.resetRequest(email.trim().toLowerCase());
      setNotice(
        res.delivery === "unavailable"
          ? "L'invio email non è configurato su questa piattaforma: contatta il tuo fornitore per reimpostare la password."
          : "Se l'indirizzo corrisponde a un Account Tray, riceverai una email con il link per reimpostare la password.",
      );
    } catch (err) {
      setError(mapError(err));
    } finally {
      setBusy(false);
    }
  }

  function signOut() {
    setIdentityToken(null);
    setStage({ kind: "credentials" });
    setNotice(null);
    setError(null);
  }

  return (
    <div className="grid min-h-screen place-items-center bg-background p-4">
      <div className="w-full max-w-md rounded-xl border border-border/60 bg-surface p-8 shadow-lg">
        <div className="mb-6 flex items-center gap-2.5">
          <img src="/tray-logo.svg" alt="" aria-hidden="true" className="h-7 w-7 shrink-0" />
          <span className="text-base font-semibold tracking-tight">Tray</span>
        </div>

        {stage.kind === "boot" && (
          <p className="text-sm text-muted-foreground">
            {t("unifiedLogin.loading", "Caricamento…")}
          </p>
        )}

        {stage.kind === "credentials" && (
          <>
            <h1 className="text-lg font-semibold text-foreground">
              {t("unifiedLogin.title", "Accedi")}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("unifiedLogin.subtitleIdentity", "Entra con il tuo Account Tray.")}
            </p>

            <form onSubmit={handleCredentials} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="ul-email">{t("unifiedLogin.email", "Email")}</Label>
                <Input
                  id="ul-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                  autoFocus
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="ul-password">{t("unifiedLogin.password", "Password")}</Label>
                <Input
                  id="ul-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </div>

              {error && (
                <Alert tone="danger" title={t("unifiedLogin.errorTitle", "Errore")}>
                  {error}
                </Alert>
              )}

              <Button type="submit" disabled={busy} className="w-full">
                {busy
                  ? t("unifiedLogin.signingIn", "Accesso…")
                  : t("unifiedLogin.submit", "Accedi")}
              </Button>

              <button
                type="button"
                className="block w-full text-center text-sm text-muted-foreground underline-offset-2 hover:underline"
                onClick={() => {
                  setError(null);
                  setNotice(null);
                  setStage({ kind: "forgot" });
                }}
              >
                {t("unifiedLogin.forgot", "Password dimenticata?")}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-muted-foreground">
              {t("unifiedLogin.noAccount", "Non hai un workspace?")}{" "}
              <a href={SIGNUP_PATH} className="font-medium text-accent hover:underline">
                {t("unifiedLogin.createOne", "Creane uno")}
              </a>
            </p>
            <p className="mt-2 text-center text-xs text-muted-foreground">
              {t(
                "unifiedLogin.localHint",
                "Hai un account locale (username)? Accedi dal sottodominio del tuo workspace.",
              )}
            </p>
          </>
        )}

        {stage.kind === "totp" && (
          <form onSubmit={handleTotp} className="mt-2 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="ul-code">{t("unifiedLogin.code", "Codice di verifica")}</Label>
              <Input
                id="ul-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                {t("unifiedLogin.codeHelp", "Inserisci il codice della tua app di autenticazione.")}
              </p>
            </div>
            {error && (
              <Alert tone="danger" title={t("unifiedLogin.errorTitle", "Errore")}>
                {error}
              </Alert>
            )}
            <Button type="submit" disabled={busy} className="w-full">
              {busy
                ? t("unifiedLogin.signingIn", "Accesso…")
                : t("unifiedLogin.verify", "Verifica")}
            </Button>
          </form>
        )}

        {stage.kind === "pick" && (
          <>
            <h1 className="text-lg font-semibold text-foreground">
              {t("unifiedLogin.pickTitle", "Scegli un workspace")}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">{stage.email}</p>

            {error && (
              <div className="mt-4">
                <Alert tone="danger" title={t("unifiedLogin.errorTitle", "Errore")}>
                  {error}
                </Alert>
              </div>
            )}

            <div className="mt-5 space-y-2">
              {stage.workspaces.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  {t(
                    "unifiedLogin.noWorkspaces",
                    "Il tuo account non è ancora collegato ad alcun workspace. Chiedi un invito a un amministratore.",
                  )}
                </p>
              )}
              {stage.workspaces.map((w) => (
                <button
                  key={w.slug}
                  type="button"
                  disabled={busy}
                  onClick={() => void enter(w.slug)}
                  className="flex w-full items-center justify-between rounded-md border border-border/60 px-4 py-3 text-left hover:bg-elevated"
                >
                  <span>
                    <span className="block text-sm font-medium">{w.name}</span>
                    <span className="block text-xs text-muted-foreground">{w.slug}</span>
                  </span>
                  <span className="text-xs text-muted-foreground">→</span>
                </button>
              ))}
            </div>

            <div className="mt-6 flex items-center justify-between text-sm">
              <a
                href="/account"
                className="text-muted-foreground underline-offset-2 hover:underline"
              >
                {t("unifiedLogin.account", "Il mio account")}
              </a>
              <button
                type="button"
                onClick={signOut}
                className="text-muted-foreground underline-offset-2 hover:underline"
              >
                {t("unifiedLogin.signOut", "Esci")}
              </button>
            </div>
          </>
        )}

        {stage.kind === "confirmCentral" && (
          <>
            <h1 className="text-lg font-semibold text-foreground">
              {t("unifiedLogin.confirmCentralTitle", "Continua su Tray Central")}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("unifiedLogin.confirmCentralAs", "Stai per entrare come")}
            </p>
            <p className="mt-3 rounded-md border border-border/60 bg-elevated px-4 py-3 text-sm font-medium text-foreground">
              {stage.email}
            </p>

            {error && (
              <div className="mt-4">
                <Alert tone="danger" title={t("unifiedLogin.errorTitle", "Errore")}>
                  {error}
                </Alert>
              </div>
            )}

            <Button
              type="button"
              disabled={busy}
              className="mt-5 w-full"
              onClick={() => void enterCentral()}
            >
              {busy
                ? t("unifiedLogin.signingIn", "Accesso…")
                : t("unifiedLogin.confirmCentralGo", "Continua")}
            </Button>
            <button
              type="button"
              disabled={busy}
              onClick={signOut}
              className="mt-3 block w-full text-center text-sm text-muted-foreground underline-offset-2 hover:underline"
            >
              {t("unifiedLogin.useAnotherAccount", "Usa un altro account")}
            </button>
          </>
        )}

        {stage.kind === "forgot" && (
          <>
            <h1 className="text-lg font-semibold text-foreground">
              {t("unifiedLogin.forgotTitle", "Reimposta la password")}
            </h1>
            <form onSubmit={handleForgot} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="ul-reset-email">{t("unifiedLogin.email", "Email")}</Label>
                <Input
                  id="ul-reset-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                  autoFocus
                />
              </div>
              {notice && <Alert tone="info">{notice}</Alert>}
              {error && <Alert tone="danger">{error}</Alert>}
              <Button type="submit" disabled={busy} className="w-full">
                {busy ? "…" : t("unifiedLogin.sendReset", "Invia il link di reset")}
              </Button>
              <button
                type="button"
                className="block w-full text-center text-sm text-muted-foreground underline-offset-2 hover:underline"
                onClick={() => setStage({ kind: "credentials" })}
              >
                {t("unifiedLogin.backToLogin", "Torna all'accesso")}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
